/**
 * PATH: packages/quote-builder/src/grid/a1Workbook.ts
 * FILE_ID: QB-GRID-A1WORKBOOK-001
 * PURPOSE: Free-form A1 workbook computation on THE shared quote-builder
 *   formula engine (FormulaJS-backed evaluateSheetFormula). Input: a map
 *   of A1 cell keys → RAW strings (literals or "=formula"). Output: the
 *   computed display value + numeric value + Excel-style error per cell.
 * WHY: ARC-3 (U06). Replaces the deleted kanban-local formula engine
 *   (packages/ui/src/kanban/workbook/computeWorkbook.ts) so the kanban
 *   calculator computes with the SAME evaluator as the quote builder —
 *   one formula library across the platform, per the operator mandate.
 * FLOW:
 *   ┌───────────────┐   ┌──────────────────────────────┐   ┌───────────────────┐
 *   │ cells {A1:..} │ → │ expand ranges → topo order →  │ → │ {values, numeric, │
 *   │ raw strings   │   │ evaluateSheetFormula per cell │   │  errors} per cell │
 *   └───────────────┘   └──────────────────────────────┘   └───────────────────┘
 * DEPENDENCIES:
 *   - ../formula/sheetFormula: the shared evaluator (Decimal + FormulaJS)
 *   - ./a1: A1 addressing helpers
 */
import {
  evaluateSheetFormula,
  extractSheetFormulaRefs,
  isSheetFormula,
} from '../formula/sheetFormula'
import { expandRange, parseRef } from './a1'
import {
  humanizeA1FormulaError,
  repairA1FormulaBody,
  transformOutsideStrings,
} from './formulaRepair'

export interface A1WorkbookData {
  /** RAW cell text keyed by normalised A1 ref (e.g. "B3"). */
  cells: Record<string, string>
  rows: number
  cols: number
}

export interface A1WorkbookComputeResult {
  /** Display string per non-empty cell (number formatted, text as-is, or error code). */
  values: Record<string, string>
  /** Numeric value per cell that resolved to a number (for downstream rollups). */
  numeric: Record<string, number>
  /** Excel-style error code per cell that failed to evaluate. */
  errors: Record<string, string>
  /** Human-readable explanation per errored cell (tooltips / formula bar). */
  errorDetails: Record<string, string>
}

/** Numeric interpretation of a literal cell; forgiving of $/€/£ and commas. */
export function parseNumericCell(raw: string): number | null {
  const t = raw.trim()
  if (t === '') return null
  const cleaned = t.replace(/^[$£€]/, '').replace(/,/g, '')
  if (cleaned === '' || /[^0-9.\-+eE]/.test(cleaned)) return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

function formatNumber(n: number): string {
  if (Number.isInteger(n)) return String(n)
  // Trim float noise to ~10 significant digits, drop trailing zeros.
  return String(parseFloat(n.toPrecision(10)))
}

const RANGE_RE = /([A-Za-z]+\d+)\s*:\s*([A-Za-z]+\d+)/g

/**
 * Pre-expand A1 ranges into comma lists so the shared tokenizer (which has
 * no range operator) can evaluate them: "SUM(A1:B2)" → "SUM(A1,B1,A2,B2)".
 * Cells in a range that hold TEXT or are blank are skipped (Excel SUM/AVG
 * semantics); a fully-skipped range contributes no arguments.
 */
export function expandA1Ranges(
  expr: string,
  isSummable: (ref: string) => boolean,
): string {
  // Never rewrite "A1:B2"-looking text INSIDE string literals.
  return transformOutsideStrings(expr, (seg) =>
    seg.replace(RANGE_RE, (whole, a: string, b: string) => {
      const keys = expandRange(a, b).filter((k) => isSummable(k))
      return keys.length > 0 ? keys.join(',') : '0'
    }),
  )
}

/**
 * Pure: compute every cell of a free-form A1 workbook.
 *
 * Semantics (parity with the deleted kanban fork, engine parity with the
 * quote builder):
 *  - literal numeric cells (incl. "$1,234.5") → numeric + verbatim display
 *  - literal text cells → verbatim display, no numeric value
 *  - "=formula" cells → evaluated by evaluateSheetFormula with a symbol
 *    table of every resolvable cell; ranges pre-expanded
 *  - blank refs in arithmetic resolve to 0; text refs → #VALUE!
 *  - cross-cell cycles → #CYCLE! on every cell in the cycle
 *  - division by zero → #DIV/0!
 *  - recoverable typos ("=SUM(A2", "=1+") are repaired before evaluation
 *    (Excel commit forgiveness); unrecoverable failures carry a human
 *    explanation in errorDetails
 */
export function computeA1Workbook(
  cells: Record<string, string>,
  /**
   * MR6 (cross-sheet) — pre-resolved EXTERNAL symbols (e.g. "xs_0" holding
   * Sheet2!A1's computed value) merged into every cell's symbol table.
   * Keys must be lowercase valid engine identifiers, NOT A1 refs.
   */
  extraSymbols: Record<string, number | string> = {},
): A1WorkbookComputeResult {
  const values: Record<string, string> = {}
  const numeric: Record<string, number> = {}
  const errors: Record<string, string> = {}
  const errorDetails: Record<string, string> = {}

  const raws = new Map<string, string>()
  for (const [k, v] of Object.entries(cells)) {
    if (typeof v === 'string' && v.trim() !== '' && parseRef(k)) {
      raws.set(k.toUpperCase(), v)
    }
  }

  // Pass 1: literals.
  const literalNumbers = new Map<string, number>()
  const textCells = new Set<string>()
  const formulaCells: string[] = []
  for (const [key, raw] of raws) {
    // A lone "=" (or "= ", or "=+" etc. with no usable body even after
    // repair) is a formula still being composed, not a broken one —
    // display it verbatim instead of shouting #ERROR!.
    if (
      isSheetFormula(raw) &&
      raw.trim().length > 1 &&
      repairA1FormulaBody(raw.trim().slice(1)) !== ''
    ) {
      formulaCells.push(key)
      continue
    }
    values[key] = raw
    const n = parseNumericCell(raw)
    if (n !== null) {
      numeric[key] = n
      literalNumbers.set(key, n)
    } else {
      textCells.add(key)
    }
  }

  // A range cell is summable when it currently holds a number or a formula
  // (formulas resolve before dependents via the topological pass below).
  const isSummable = (ref: string) => {
    const raw = raws.get(ref.toUpperCase())
    if (raw === undefined) return false
    return !textCells.has(ref.toUpperCase())
  }

  // Pass 2: expand ranges + build the dependency graph for formula cells.
  const expanded = new Map<string, string>()
  const deps = new Map<string, string[]>()
  for (const key of formulaCells) {
    const raw = raws.get(key)!
    // Commit forgiveness: repair recoverable typos (unclosed parens,
    // dangling operators) BEFORE range expansion, so "=SUM(A2" computes.
    const body = repairA1FormulaBody(raw.trim().slice(1))
    const expr = expandA1Ranges(body, isSummable)
    expanded.set(key, expr)
    const refs = extractSheetFormulaRefs(expr)
      .map((r) => r.toUpperCase())
      .filter((r) => parseRef(r) !== null)
    deps.set(key, refs)
  }

  // Pass 3: topological evaluation with cycle detection (DFS).
  const resolved = new Map<string, number | string>() // formula results
  const visiting = new Set<string>()
  const done = new Set<string>()

  const CYCLE = '#CYCLE!'

  function evalCell(key: string): void {
    if (done.has(key)) return
    if (visiting.has(key)) {
      errors[key] = CYCLE
      values[key] = CYCLE
      errorDetails[key] = 'Circular reference — this cell depends on itself'
      done.add(key)
      return
    }
    visiting.add(key)
    try {
      for (const dep of deps.get(key) ?? []) {
        if (expanded.has(dep)) evalCell(dep)
      }
      if (errors[key] === CYCLE) return

      // Build the symbol table for THIS cell: every literal number, every
      // resolved formula, TEXT cells as strings (so & concat and text
      // functions work; arithmetic on them raises #VALUE), and 0 for
      // blank refs.
      const symbols: Record<string, number | string> = { ...extraSymbols }
      for (const [k, v] of literalNumbers) symbols[k.toLowerCase()] = v
      for (const t of textCells) symbols[t.toLowerCase()] = raws.get(t) ?? ''
      for (const [k, v] of resolved) symbols[k.toLowerCase()] = v
      for (const dep of deps.get(key) ?? []) {
        const up = dep.toUpperCase()
        if (!raws.has(up) && parseRef(up)) symbols[up.toLowerCase()] = 0
        if (errors[up] && !(up in symbols)) {
          // A dependency errored — propagate its code + explanation.
          errors[key] = errors[up]
          values[key] = errors[up]
          errorDetails[key] = `Depends on ${up}, which has an error`
          done.add(key)
          return
        }
      }

      const result = evaluateSheetFormula(expanded.get(key)!, symbols, {
        allowText: true,
      })
      if (result.ok) {
        resolved.set(key, result.value)
        if (typeof result.value === 'string') {
          // Text-valued formula (CONCAT, IF text branch, "…" literal).
          values[key] = result.value
        } else {
          numeric[key] = result.value
          values[key] = formatNumber(result.value)
        }
      } else {
        // NB: repo tsconfig has strict:false, which breaks discriminated-union
        // narrowing on `result.ok` — hence the explicit failure shape.
        const failure = result as { ok: false; code: string; message: string }
        const code =
          failure.code === '#CIRC'
            ? CYCLE
            : failure.code === '#REF' || failure.code === '#VALUE'
              ? '#VALUE!'
              : /division by zero/i.test(failure.message)
                ? '#DIV/0!'
                : '#ERROR!'
        errors[key] = code
        values[key] = code
        errorDetails[key] = humanizeA1FormulaError(failure.message)
      }
    } finally {
      visiting.delete(key)
      done.add(key)
    }
  }

  for (const key of formulaCells) evalCell(key)

  return { values, numeric, errors, errorDetails }
}

/** Coerce an unknown stored blob into valid A1WorkbookData (defensive read). */
export function normalizeA1Workbook(
  raw: unknown,
  fallbackRows = 24,
  fallbackCols = 8,
): A1WorkbookData {
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const cellsIn = (obj.cells && typeof obj.cells === 'object' ? obj.cells : {}) as Record<
    string,
    unknown
  >
  const cells: Record<string, string> = {}
  for (const [k, v] of Object.entries(cellsIn)) {
    if (parseRef(k) && (typeof v === 'string' || typeof v === 'number')) {
      const s = String(v)
      if (s !== '') cells[k.toUpperCase()] = s
    }
  }
  const rows = Number.isFinite(obj.rows)
    ? Math.max(1, Math.min(200, Number(obj.rows)))
    : fallbackRows
  const cols = Number.isFinite(obj.cols)
    ? Math.max(1, Math.min(52, Number(obj.cols)))
    : fallbackCols
  return { cells, rows, cols }
}
